import Head from 'next/head';
import Script from 'next/script';

import {
  useStorefrontInit,
  useCartAddDiscountUrl,
} from '@backpackjs/storefront';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Header from '../snippets/globals/layout/Header';
import Footer from '../snippets/globals/layout/Footer';
import RootStyles from '../snippets/globals/RootStyles';
import DiscountBanner from '@/snippets/globals/layout/DiscountBanner';
import { DataLayerWithElevar } from '@/snippets/components/DataLayerWithElevar';
import HeadMeta from '@/snippets/globals/layout/HeadMeta';
import GeoLocation from '@/snippets/components/GeoLocation';
import { useShopifyAnalytics } from '@hooks/shopifyAnalytics';

import { QuickViewModal } from '@/snippets/globals/layout/QuickViewModal';

function StorefrontLayout({ ...props }) {
  const { ContentForLayout, settings, page } = props;
  const { events } = useRouter();
  // initialize customer, cart, shop and markets
  useStorefrontInit();

  // send page view and add to cart events to Shopify analytics
  useShopifyAnalytics({ collection: props.collection, product: props.product });

  // auto applies cart discount found on the landing url
  useCartAddDiscountUrl();

  const fadeIntoWhite = () => {
    document.querySelector('main').classList.add('is-loading');
  };

  const fadeOutOfWhite = () => {
    document.querySelector('main').classList.remove('is-loading');
  };

  useEffect(() => {
    events.on('routeChangeStart', fadeIntoWhite);
    events.on('routeChangeComplete', fadeOutOfWhite);

    return () => {
      events.off('routeChangeStart', fadeIntoWhite);
      events.off('routeChangeComplete', fadeOutOfWhite);
    };
  });

  return (
    <>
      <Head>
        <HeadMeta />
        <meta
          key="page:fb:image"
          property="og:image"
          content={
            page?.seo?.image ||
            props.product?.featuredImage?.src ||
            settings?.settings?.globalShareImage?.image?.src
          }
        />
      </Head>

      <DataLayerWithElevar />
      <Script
        type="text/javascript"
        id="gtm-script"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_CONTAINER_ID}');`,
        }}
      />
      <Script
        defer={false}
        dangerouslySetInnerHTML={{
          __html: `!function(){var t=[
            "https://st.pandect.es/briogeo-hair-care/pandectes-rules.js",
            "https://s.pandect.es/c/pandectes-core.js?storeId=9053204"]
            ;function e(t,e){var a=document.createElement("script");
            a.src=t,a.async=!0,a.type="text/javascript",e&&(a.onload=e);
            var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(a,s)}
            var a=function(){e(t[0],function(){e(t[1])})};
            window.attachEvent?window.attachEvent("onload",a):window.addEventListener("load",a,!1)}();`,
        }}
        id="pandectes"
      />
      <Script
        src="https://dev.visualwebsiteoptimizer.com/lib/318949.js"
        id="vwoCode"
        strategy="beforeInteractive"
        defer={false}
      />

      <Script
        src="https://js.afterpay.com/afterpay-1.x.js"
        strategy="beforeInteractive"
      />
      <Script
        src="https://cdn-widgetsrepository.yotpo.com/v1/loader/sjzt7N108GUzJ96g4WqUxg"
        async
      />

      <Script
        dangerouslySetInnerHTML={{
          __html: `window.Shopify = {
              shop: "briogeo-hair-care.myshopify.com",
              currency: {
                active: "USD",
                rate: "1.0"
              }
            }`,
        }}
      />

      <Script id="beamCartImport"
              type="module"
              dangerouslySetInnerHTML={{__html: `
        import { updateCart } from 'https://sdk.beamimpact.com/web-sdk/v1.40.0/dist/integrations/cart.js'
        window.beamUpdateCart = updateCart;
      `}} />

      {/* Global-e replacement cookie script */}
      <Script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          var createReplacementCookie = function (cookieDomain) {
            function getParameterByName(name, url = window.location.href) {
                name = name.replace(/[\\[\\]]/g, '\\$&');
                var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'), results = regex.exec(url);
                if (!results) return null;
                if (!results[2]) return '';
                return decodeURIComponent(results[2].replace(/\\+/g, ' '));
            }

            function ticksToDate(ticks) {
                return Number.isInteger(ticks) ? new Date(ticks / 1e+4 + new Date('0001-01-01T00:00:00Z').getTime()) : null;
            }

            //* If URL contains the query parameter replacementExpire, create cookie GE_Replacement
            let replacementExpireParam = parseInt(getParameterByName('replacementExpire'));
            if (replacementExpireParam !== null && replacementExpireParam > Date.now()) {
                var cookieStringifiedValue = encodeURIComponent(JSON.stringify({ glCountry: getParameterByName('glCountry'), glCurrency: getParameterByName('glCurrency') }));
                var expirationInUTC = ticksToDate(replacementExpireParam).toUTCString();
                document.cookie = 'GE_Replacement=' + cookieStringifiedValue + ';expires=' + expirationInUTC + ';path=/;domain=.' +cookieDomain;
              }
            };

            var cookieDomain = window.location.hostname
            //can require custom value on stores whene checkout page domain is different
            createReplacementCookie(cookieDomain);`,
        }}
      />

      <RootStyles />
      <DiscountBanner />

      <Header />

      <main className="main-container">
        <ContentForLayout {...props} />
      </main>
      <QuickViewModal />
      <Footer />
      <GeoLocation />

      <Script
        type="text/javascript"
        src="https://cdn.swellrewards.com/loader/sjzt7N108GUzJ96g4WqUxg.js"
      />

      <Script src="https://cdn.crazyrocket.io/widget/scripts/crazyrocket.start.js?id=64e37206117cee01887d7b67&shop=briogeo-hair-care.myshopify.com" />

      <Script
        id="gorgias-chat-widget-install-v2"
        src="https://config.gorgias.chat/gorgias-chat-bundle-loader.js?applicationId=10623"
      />
      <Script src="https://static.myshlf.us/Affiliates/sms_aff_clicktrack.js" />
      <Script
        async
        type="text/javascript"
        src="https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=H6yLPD"
      />
      <Script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          (function(){ var s = document.createElement('script'); var h = document.querySelector('head') || document.body; s.src = 'https://acsbapp.com/apps/app/dist/js/app.js'; s.async = true; s.onload = function(){ acsbJS.init({ statementLink : '', footerHtml : '', hideMobile : false, hideTrigger : false, disableBgProcess : false, language : 'en', position : 'left', leadColor : '#444444', triggerColor : '#444444', triggerRadius : '50%', triggerPositionX : 'left', triggerPositionY : 'bottom', triggerIcon : 'people', triggerSize : 'bottom', triggerOffsetX : 20, triggerOffsetY : 80, mobile : { triggerSize : 'small', triggerPositionX : 'left', triggerPositionY : 'bottom', triggerOffsetX : 10, triggerOffsetY : 80, triggerRadius : '20' } }); }; h.appendChild(s); })();`,
        }}
      />

      {/* Temporary removal of the cart widget
      <Script async
        fetchOnMount={true}
        src='https://code.buywithprime.amazon.com/bwp.v1.js'/>
      <div
          id="amzn-bwp-cart"
          data-site-id="gwfxnozf54"
          data-widget-id="w-XRZsc7Ov0c7a7UehqIg5N0"
      ></div> */}
    </>
  );
}

// eslint-disable-next-line import/no-default-export
export default StorefrontLayout;
